import { AlertCircle, Zap } from 'lucide-react';
import React, { useState } from 'react';
import PlanGraph from './PlanGraph';
import { PlanStep } from './types';

interface ConfirmationCardProps {
  payload: any;
  runId: string;
  isPending: boolean;
  confirmed?: boolean;
  onConfirmAction: (id: string, confirmed: boolean) => void;
  slotInputs: any;
  setSlotInputs: any;
}

export default function ConfirmationCard({ payload, runId, isPending, confirmed, onConfirmAction, slotInputs, setSlotInputs }: ConfirmationCardProps) {
  // Use local state for typing to prevent global re-renders on every keystroke
  // We flush to global state on change but rely on React.memo in other components to prevent lag
  const plan = (payload?.plan ?? []) as PlanStep[];
  const implicitCreates = payload?.implicit_creates ?? [];
  const allMissing = payload?.missing ?? [];

  if (payload?.type === 'slot_collection') {
    const teamCtx = payload.team_context ?? {};
    const missing = (payload.missing ?? []) as { param: string; label: string; required: boolean }[];
    const optional = (payload.optional ?? []) as { param: string; label: string }[];

    // We can use local state for immediate typing feedback
    const [localSlots, setLocalSlots] = useState<Record<string, string>>(slotInputs[runId]?.['midplan'] ?? {});

    const canSubmit = missing.every(m => !m.required || !!localSlots[m.param]?.trim());

    const handleChange = (param: string, value: string) => {
      setLocalSlots(prev => ({ ...prev, [param]: value }));
      // Also flush to global state so it's ready when submitting
      setSlotInputs((prev: any) => ({
        ...prev,
        [runId]: { ...(prev[runId] ?? {}), midplan: { ...(prev[runId]?.midplan ?? {}), [param]: value } },
      }));
    };

    return (
      <div className="bx-confirm-card">
        {isPending ? (
          <>
            <div className="bx-modal-header">
              <div className="bx-modal-title">
                <Zap size={13} />
                <span>Datos necesarios</span>
              </div>
            </div>

            {teamCtx.team_count != null && (
              <div style={{ padding: '8px 0 4px', fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                {teamCtx.team_count === 1
                  ? <>Equipo encontrado: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{teamCtx.team_name || `ID ${teamCtx.team_id}`}</strong></>
                  : <>{teamCtx.team_count} equipos: {(teamCtx.all_names ?? []).join(', ')}</>
                }
                {teamCtx.description && (
                  <span style={{ color: 'rgba(255,255,255,0.35)', marginLeft: 6 }}>— {teamCtx.description}</span>
                )}
              </div>
            )}

            <div className="bx-confirm-missing" style={{ marginTop: 8 }}>
              <div className="bx-confirm-missing-label">Parámetros requeridos</div>
              {missing.map(m => (
                <div key={m.param} className="bx-confirm-missing-row">
                  <span className="bx-confirm-missing-name">{m.label}</span>
                  <input
                    className="bx-confirm-param-input bx-confirm-param-input--required"
                    type="text"
                    value={localSlots[m.param] ?? ''}
                    placeholder={m.label}
                    onChange={e => handleChange(m.param, e.target.value)}
                  />
                </div>
              ))}

              {optional.map(o => (
                <div key={o.param} className="bx-confirm-missing-row">
                  <span className="bx-confirm-missing-name" style={{ opacity: 0.6 }}>{o.label} <em>(opcional)</em></span>
                  <input
                    className="bx-confirm-param-input"
                    type="text"
                    value={localSlots[o.param] ?? ''}
                    placeholder={`${o.label} (opcional)`}
                    onChange={e => handleChange(o.param, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <div className="bx-confirm-actions">
              <button className="bx-confirm-reject" onClick={() => onConfirmAction(runId, false)}>
                Cancel
              </button>
              <button
                className="bx-confirm-accept bx-confirm-accept--full"
                disabled={!canSubmit}
                style={!canSubmit ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                onClick={() => canSubmit && onConfirmAction(runId, true)}
              >
                Apply changes
              </button>
            </div>
          </>
        ) : (
          <div className={`bx-confirm-result${confirmed ? ' bx-confirm-result--accepted' : ' bx-confirm-result--rejected'}`}>
            {confirmed ? '✓ Applied' : '✗ Cancelled'}
          </div>
        )}
      </div>
    );
  }

  if (payload?.type === 'authorize_create') {
    return (
      <div className="bx-confirm-card">
        {isPending ? (
          <>
            <div className="bx-modal-header">
              <div className="bx-modal-title">
                <AlertCircle size={13} />
                <span>Authorization required</span>
              </div>
            </div>
            <p className="bx-confirm-summary" style={{ marginTop: 8 }}>
              {payload.message || `To continue I need to create a ${payload.entity || 'resource'}. Do you want me to create it?`}
            </p>
            <div className="bx-confirm-actions">
              <button className="bx-confirm-reject" onClick={() => onConfirmAction(runId, false)}>
                No, cancel
              </button>
              <button className="bx-confirm-accept bx-confirm-accept--full" onClick={() => onConfirmAction(runId, true)}>
                Yes, create
              </button>
            </div>
          </>
        ) : (
          <div className={`bx-confirm-result${confirmed ? ' bx-confirm-result--accepted' : ' bx-confirm-result--rejected'}`}>
            {confirmed ? '✓ Creation authorized' : '✗ Creation cancelled — process aborted'}
          </div>
        )}
      </div>
    );
  }

  if (payload?.type === 'validation_failed') {
    return (
      <div className="bx-confirm-card">
        {isPending ? (
          <>
            <div className="bx-modal-header">
              <div className="bx-modal-title">
                <AlertCircle size={13} />
                <span>Validation failed</span>
              </div>
            </div>
            <p className="bx-confirm-summary" style={{ marginTop: 8 }}>
              {payload.message}
            </p>
            <div className="bx-confirm-actions">
              <button className="bx-confirm-reject" onClick={() => onConfirmAction(runId, false)}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="bx-confirm-result bx-confirm-result--rejected">
            ✗ Aborted — validation failed
          </div>
        )}
      </div>
    );
  }

  const humanMissing = allMissing.filter((p: any) => !/_id$/i.test(p.param) && p.param !== 'id');

  // Standard plan confirmation
  return (
    <div className="bx-confirm-card">
      {isPending ? (
        <>
          <div className="bx-modal-header">
            <div className="bx-modal-title">
              <Zap size={13} />
              <span>Plan de ejecución</span>
            </div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
              {plan.length} pasos
            </span>
          </div>

          <PlanGraph steps={plan} />

          {implicitCreates.length > 0 && (
            <div className="bx-confirm-implicit">
              <div className="bx-confirm-implicit-header">
                <AlertCircle size={12} />
                <span>También creará nuevos recursos (si no existen):</span>
              </div>
              <div className="bx-confirm-implicit-items">
                {implicitCreates.map((ic: any, idx: number) => (
                  <div key={idx} className="bx-confirm-implicit-item">
                    <span>{ic.label}</span>
                    {ic.condition && <span className="bx-confirm-implicit-cond">{ic.condition}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {humanMissing.length > 0 && (
            <div className="bx-confirm-missing">
              <div className="bx-confirm-missing-label">Parámetros requeridos</div>
              {humanMissing.map((p: any) => {
                const val = slotInputs[runId]?.[String(p.step_idx)]?.[p.param] ?? '';
                return (
                  <div key={`${p.step_idx}-${p.param}`} className="bx-confirm-missing-row">
                    <span className="bx-confirm-missing-name">{p.label ?? p.param}</span>
                    <input
                      className="bx-confirm-param-input bx-confirm-param-input--required"
                      type="text"
                      value={val}
                      placeholder={p.question ?? p.label ?? p.param}
                      onChange={e => {
                        const v = e.target.value;
                        setSlotInputs((prev: any) => ({
                          ...prev,
                          [runId]: {
                            ...(prev[runId] ?? {}),
                            [String(p.step_idx)]: { ...((prev[runId] ?? {})[String(p.step_idx)] ?? {}), [p.param]: v },
                          },
                        }));
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div className="bx-confirm-actions">
            <button className="bx-confirm-reject" onClick={() => onConfirmAction(runId, false)}>
              Cancel
            </button>
            <button
              className="bx-confirm-accept bx-confirm-accept--full"
              disabled={humanMissing.some((p: any) => !slotInputs[runId]?.[String(p.step_idx)]?.[p.param]?.trim())}
              style={humanMissing.some((p: any) => !slotInputs[runId]?.[String(p.step_idx)]?.[p.param]?.trim()) ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
              onClick={() => {
                if (humanMissing.every((p: any) => !!slotInputs[runId]?.[String(p.step_idx)]?.[p.param]?.trim())) {
                  onConfirmAction(runId, true);
                }
              }}
            >
              {implicitCreates.length > 0 ? 'Yes, proceed' : `Authorize ${plan.length} ${plan.length === 1 ? 'step' : 'steps'}`}
            </button>
          </div>
        </>
      ) : (
        <div className={`bx-confirm-result${confirmed ? ' bx-confirm-result--accepted' : ' bx-confirm-result--rejected'}`}>
          {confirmed ? '✓ Authorized' : '✗ Cancelled'}
        </div>
      )}
    </div>
  );
}
